/*
 * KACHEL-PROTOKOLL — was in einer Browser-Kachel einer Guide-Buehne passiert ist
 * (Rafael, 07.10.2026 06:58Z: „so dass wenn ich beim tab irgendwas klicke du nachher auch
 * weißt was ich beantragt hab"; Auftrag privat/gewinnspiele/auftrag-tab-protokoll.md).
 *
 * Warum es das braucht: Rafael loest Captcha und schickt ab; sobald er auf der Buehne
 * antwortet, springt der Guide weiter und die Kacheln werden ausgehaengt. Ein Waechter per
 * /api/browser-panel/…/eval erreicht eine Kachel nur, solange sie montiert ist. Das
 * Protokoll schreibt deshalb die KACHEL selbst, waehrend sie lebt, an den Server
 * (server/lib/kachel-protokoll.ts) — gelesen wird spaeter, unabhaengig vom Montiertsein.
 *
 * DER WEG AUS DER SEITE: Long-Poll ueber `executeJavaScript`, NICHT ueber console.log.
 * Die Konsole der Seite gehoert der Seite: Fehlerdienste (Sentry u. a.) haengen sich an
 * `console.*` und schicken Breadcrumbs an Dritte — Rafaels Formularwerte duerfen da nie
 * durchlaufen. Das Ergebnis eines `executeJavaScript`-Promise geht dagegen direkt per IPC
 * an die App. Der Waechter haengt seine Ereignisse an eine Schlange; der naechste offene
 * Abruf bekommt sie sofort (Mikrotask nach dem submit-Ereignis, also VOR dem Seitenwechsel).
 *
 * WAS NIE DIE SEITE VERLAESST: Passwortfelder, versteckte Felder (fast immer Tokens),
 * Datei-Felder, Kartennummern/-pruefziffern, Einmalcodes und alles, dessen Name nach
 * Captcha/Token/CSRF aussieht (GEHEIM_MUSTER). Ausgelassene Felder werden nur GEZAEHLT.
 * Der Server prueft dieselbe Regel ein zweites Mal (feldIstGeheim) — Verteidigung in der
 * Tiefe, falls ein Client einmal etwas anderes schickt.
 *
 * Grenze, ehrlich: `executeJavaScript` erreicht nur das Hauptdokument. Formulare in
 * FREMDEN Iframes (cross-origin) sieht der Waechter nicht; Navigationen und die Textprobe
 * des Hauptdokuments schon.
 *
 * Dieses Modul ist rein (kein DOM, kein fs): Server und Client teilen es.
 */

/** Feldnamen, deren Wert nie protokolliert wird. Quelle fuer Seite (als RegExp-Text) UND Server. */
export const GEHEIM_MUSTER = 'captcha|recaptcha|hcaptcha|turnstile|cf-chl|token|csrf|xsrf|nonce|authenticity|passw|kennwort|secret|otp|tan\\b|pin\\b|iban|cvc|cvv|kartennummer|card.?number';
const GEHEIM = new RegExp(GEHEIM_MUSTER, 'i');
const GEHEIME_TYPEN = new Set(['password', 'hidden', 'file']);

/** Kennung einer protokollierten Kachel: nur Browser-Kacheln einer Guide-Buehne (shared/guide-kachel-id.ts). */
const KACHEL_ID = /^guide-p-browser-\d{1,3}-[0-9a-z]{7}$/;

/** `#guide-p-browser-0-abc1234` und `guide-p-browser-0-abc1234` sind dieselbe Kachel. */
export function normalisiereKachelId(panelId: string): string {
  return panelId.startsWith('#') ? panelId.slice(1) : panelId;
}

export function istProtokollKachel(panelId: string | undefined | null): boolean {
  return !!panelId && KACHEL_ID.test(normalisiereKachelId(panelId));
}

export function feldIstGeheim(f: { name?: string; typ?: string }): boolean {
  return GEHEIME_TYPEN.has(String(f.typ ?? '').toLowerCase()) || GEHEIM.test(String(f.name ?? ''));
}

export interface ProtokollFeld { name: string; typ: string; beschriftung?: string; wert: string }
export interface ProtokollFormular { aktion: string; methode: string; name: string }

export type ProtokollEintrag =
  | { art: 'seite'; at: string; url: string; titel: string }
  | { art: 'absendung' | 'klick'; at: string; id: string; url: string; knopf: string;
      formular: ProtokollFormular | null; felder: ProtokollFeld[]; ausgelassen: number }
  | { art: 'textprobe'; at: string; zu: string; url: string; titel: string;
      meldungen: string[]; treffer: string[]; anfang: string }
  | { art: 'fehler'; at: string; text: string };

/** Obergrenzen — gelten in der Seite UND beim Schreiben auf dem Server. */
export const GRENZEN = {
  feldwert: 300,
  felder: 60,
  url: 500,
  kurztext: 200,
  anfang: 400,
  /** Eintraege je Kachel; aeltere fallen vorne heraus. */
  jeKachel: 300,
  /** Kacheln in der Datei; die am laengsten nicht beschriebenen fallen heraus. */
  kacheln: 400,
  /** Tage, nach denen eine nicht mehr beschriebene Kachel geloescht wird. */
  tage: 60,
} as const;

/**
 * Das Skript, das IN der Seite laeuft. Beim ersten Aufruf je Dokument haengt es zwei
 * Lauscher (submit, click — beide in der Capture-Phase, beide nur lesend: kein
 * preventDefault, kein stopPropagation) und legt seinen Zustand unter einem zufaelligen,
 * nicht aufzaehlbaren Schluessel ab. Jeder Aufruf liefert ein Promise, das mit den
 * aufgelaufenen Ereignissen aufloest — sofort, wenn schon welche da sind, sonst beim
 * naechsten Ereignis, spaetestens nach `wachMs` mit [] (Herzschlag, damit ein Abruf
 * nie ewig haengt). Ein neuer Abruf loest einen noch offenen mit [] ab.
 */
export function baueWaechterSkript(schluessel: string, wachMs = 25_000): string {
  return `(function (KEY, GEHEIM_QUELLE, G, WACH) {
  var st = window[KEY];
  if (!st) {
    st = { q: [], warte: null, n: 0, absendungen: 0 };
    Object.defineProperty(window, KEY, { value: st, enumerable: false, configurable: true });
    var GEHEIM = new RegExp(GEHEIM_QUELLE, 'i');
    var kurz = function (s, n) { s = String(s == null ? '' : s).replace(/\\s+/g, ' ').trim(); return s.length > n ? s.slice(0, n) + '…' : s; };
    var neueId = function () { st.n += 1; return 'e' + Date.now().toString(36) + '-' + st.n + Math.random().toString(36).slice(2, 6); };
    var beschriftung = function (el) {
      try { if (el.labels && el.labels.length) return kurz(el.labels[0].innerText || el.labels[0].textContent, 80); } catch (e) {}
      return kurz(el.getAttribute('aria-label') || el.getAttribute('placeholder') || '', 80);
    };
    var knopfText = function (el) { return kurz(el.innerText || el.value || el.getAttribute('aria-label') || el.title || '', 80); };
    var felderVon = function (wurzel) {
      var out = [], aus = 0;
      var els = wurzel.tagName === 'FORM' ? wurzel.elements : wurzel.querySelectorAll('input,select,textarea');
      for (var i = 0; i < els.length && out.length < G.felder; i++) {
        var el = els[i], tag = el.tagName;
        if (tag !== 'INPUT' && tag !== 'SELECT' && tag !== 'TEXTAREA') continue;
        var typ = tag === 'INPUT' ? String(el.type || 'text').toLowerCase() : tag.toLowerCase();
        if (typ === 'submit' || typ === 'button' || typ === 'reset' || typ === 'image' || el.disabled) continue;
        var name = el.name || el.id || '';
        var ac = String(el.getAttribute('autocomplete') || '').toLowerCase();
        if (typ === 'password' || typ === 'hidden' || typ === 'file' || GEHEIM.test(name) || GEHEIM.test(el.id || '')
          || /^cc-(number|csc|exp)/.test(ac) || ac === 'current-password' || ac === 'new-password' || ac === 'one-time-code') { aus++; continue; }
        var wert;
        if (typ === 'checkbox' || typ === 'radio') { if (!el.checked) continue; wert = el.value && el.value !== 'on' ? el.value : 'an'; }
        else if (tag === 'SELECT') { wert = Array.prototype.map.call(el.selectedOptions || [], function (o) { return o.text || o.value; }).join(', '); }
        else wert = el.value;
        out.push({ name: kurz(name, 80), typ: typ, beschriftung: beschriftung(el), wert: kurz(wert, G.feldwert) });
      }
      return { felder: out, ausgelassen: aus };
    };
    var formularVon = function (f) { return f ? { aktion: kurz(f.action, G.url), methode: String(f.method || 'get').toLowerCase(), name: kurz(f.getAttribute('name') || f.id || '', 80) } : null; };
    var behaelter = function (el) {
      for (var p = el.parentElement, i = 0; p && p !== document.body && i < 8; p = p.parentElement, i++) {
        if (p.querySelector('input:not([type=hidden]):not([type=submit]):not([type=button]),select,textarea')) return p;
      }
      return null;
    };
    var melde = function (ev) {
      ev.at = new Date().toISOString();
      ev.url = kurz(location.href, G.url);
      st.q.push(ev);
      if (st.q.length > 50) st.q.shift();
      if (st.warte) { var w = st.warte, q = st.q; st.warte = null; st.q = []; w(q); }
    };
    window.addEventListener('submit', function (e) {
      try {
        var f = e.target;
        if (!f || f.tagName !== 'FORM') return;
        st.absendungen += 1;
        var s = felderVon(f);
        melde({ art: 'absendung', id: neueId(), knopf: e.submitter ? knopfText(e.submitter) : '', formular: formularVon(f), felder: s.felder, ausgelassen: s.ausgelassen });
      } catch (err) { melde({ art: 'fehler', text: 'submit: ' + ((err && err.message) || err) }); }
    }, true);
    window.addEventListener('click', function (e) {
      try {
        var t = e.target && e.target.closest ? e.target.closest('button,input[type=submit],input[type=button],input[type=image],[role=button]') : null;
        if (!t) return;
        var f = t.form || t.closest('form');
        var typ = String(t.getAttribute('type') || (t.tagName === 'BUTTON' ? 'submit' : '')).toLowerCase();
        var schicktAb = !!f && (typ === 'submit' || typ === 'image');
        var vorher = st.absendungen;
        // Nach dem Ereignis nachsehen: hat der Klick ein submit ausgeloest, steht es schon als
        // „absendung" da. Sonst (JS-Formular ohne submit) haelt der Klick den Stand fest.
        setTimeout(function () {
          try {
            if (schicktAb && st.absendungen !== vorher) return;
            var wurzel = f || behaelter(t);
            var s = wurzel ? felderVon(wurzel) : { felder: [], ausgelassen: 0 };
            melde({ art: 'klick', id: neueId(), knopf: knopfText(t), formular: formularVon(f), felder: s.felder, ausgelassen: s.ausgelassen });
          } catch (err) { melde({ art: 'fehler', text: 'klick: ' + ((err && err.message) || err) }); }
        }, 0);
      } catch (err) { melde({ art: 'fehler', text: 'klick: ' + ((err && err.message) || err) }); }
    }, true);
  }
  return new Promise(function (res) {
    if (st.warte) { var alt = st.warte; st.warte = null; alt([]); }
    if (st.q.length) { var q = st.q; st.q = []; res(q); return; }
    st.warte = res;
    setTimeout(function () { if (st.warte === res) { st.warte = null; res([]); } }, WACH);
  });
})(${JSON.stringify(schluessel)}, ${JSON.stringify(GEHEIM_MUSTER)}, ${JSON.stringify({ feldwert: GRENZEN.feldwert, felder: GRENZEN.felder, url: GRENZEN.url })}, ${Number(wachMs)})`;
}

/**
 * Textprobe nach dem Absenden: was steht JETZT auf der Seite? Meldungsartige Elemente
 * (role=alert/status, aria-live, Klassen wie success/error/danke) und Zeilen mit
 * typischen Bestaetigungs-/Fehlerwoertern. Nur wenn beides leer ist, der Seitenanfang.
 * Liest nur, veraendert nichts.
 */
export const TEXTPROBE_SKRIPT = `(function (G) {
  var kurz = function (s, n) { s = String(s == null ? '' : s).replace(/\\s+/g, ' ').trim(); return s.length > n ? s.slice(0, n) + '…' : s; };
  var MUSTER = /(danke|vielen dank|thank|erfolgreich|teilgenommen|teilnahme|bestätig|bestaetig|registriert|eingegangen|angemeldet|gewonnen|fehler|error|ungültig|ungueltig|bereits|leider|pflichtfeld|required|invalid|nicht korrekt|captcha|abgelaufen)/i;
  var sichtbar = function (el) { var r = el.getBoundingClientRect(); return r.width > 0 && r.height > 0; };
  var meldungen = [], treffer = [], gesehen = {};
  var nimm = function (liste, text) { var t = kurz(text, G.kurztext); if (t.length < 3 || gesehen[t] || liste.length >= 6) return; gesehen[t] = 1; liste.push(t); };
  var kandidaten = document.querySelectorAll('[role=alert],[role=status],[aria-live],.alert,[class*="success"],[class*="error"],[class*="danke"],[class*="thank"],[class*="confirm"],[class*="meldung"],[class*="notice"]');
  for (var i = 0; i < kandidaten.length; i++) { try { if (sichtbar(kandidaten[i])) nimm(meldungen, kandidaten[i].innerText); } catch (e) {} }
  var text = document.body ? document.body.innerText || '' : '';
  var zeilen = text.split(/\\n+/);
  for (var j = 0; j < zeilen.length; j++) if (MUSTER.test(zeilen[j])) nimm(treffer, zeilen[j]);
  return { url: kurz(location.href, G.url), titel: kurz(document.title, G.kurztext), meldungen: meldungen, treffer: treffer,
    anfang: meldungen.length || treffer.length ? '' : kurz(text, G.anfang) };
})(${JSON.stringify({ url: GRENZEN.url, kurztext: GRENZEN.kurztext, anfang: GRENZEN.anfang })})`;

/** Wann nach einer Absendung/einem Klick die Seite gelesen wird (ms). */
export const TEXTPROBE_NACH_MS = [3_000, 10_000] as const;
